import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { Product } from '@/lib/types';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import ImageHandler from '@/components/ImageHandler';

interface ProductCardProps {
  product: Product;
  featured?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, featured = false }) => {
  const { addItem } = useCart();
  
  // Debug: verificar se imageUrl existe
  React.useEffect(() => {
    if (!product.imageUrl) {
      console.warn(`Produto sem imagem: ${product.name} (ID: ${product.id})`);
    }
  }, [product]);
  
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Calculate discounted price if applicable
  const finalPrice = product.discount 
    ? product.price * (1 - product.discount / 100) 
    : product.price;

  // Check if category name exists directly on the product object
  const categoryName = product.category?.name;

  return (
    <Link 
      to={`/product/${product.id}`} 
      className="group flex flex-col bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-blue-300 h-full"
    >
      {/* Imagem do Produto */}
      <div className="relative overflow-hidden bg-gray-50 aspect-square">
        <ImageHandler
          src={product.imageUrl || '/placeholder.svg'} 
          alt={product.name}
          size="medium"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        
        {/* Badges no Topo Esquerdo */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {product.discount && product.discount > 0 && (
            <span className="inline-flex items-center bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md shadow-sm">
              -{product.discount}%
            </span>
          )}
          {product.isNew && (
            <span className="inline-flex items-center bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-md shadow-sm">
              🆕 Novo
            </span>
          )}
          {product.isFeatured && (
            <span className="inline-flex items-center bg-yellow-500 text-white text-xs font-semibold px-2 py-1 rounded-md shadow-sm">
              ⭐ Destaque
            </span>
          )}
        </div>
        
        {/* Badge de Categoria no Topo Direito */}
        {categoryName && (
          <div className="absolute top-2 right-2">
            <span className="inline-block bg-navy-600/90 text-white text-xs font-medium px-2 py-1 rounded-md shadow-sm backdrop-blur-sm">
              {categoryName}
            </span>
          </div>
        )}
        
        {/* Botão Adicionar ao Carrinho */}
        <button
          onClick={handleAddToCart}
          className="absolute bottom-3 right-3 bg-orange-500 hover:bg-orange-600 text-white p-2.5 rounded-full shadow-lg transition-all duration-200 transform translate-y-12 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 hover:scale-110 active:scale-95"
          aria-label="Adicionar ao carrinho"
          title="Adicionar ao carrinho"
        >
          <ShoppingCart size={18} />
        </button>
      </div>
      
      {/* Informações do Produto */}
      <div className="p-3 flex flex-col flex-grow">
        {/* Nome do Produto */}
        <h3 className="text-gray-900 font-semibold line-clamp-2 text-sm leading-tight mb-2 min-h-[2.5rem]">
          {product.name}
        </h3>
        
        {/* Preço e Estoque */}
        <div className="mt-auto space-y-1.5">
          {/* Preço */}
          <div className="flex items-baseline gap-2">
            <span className="text-navy-700 font-bold text-lg"> 
              {formatPrice(finalPrice)}
            </span>
            {product.discount && product.discount > 0 && (
              <span className="text-gray-400 text-sm line-through"> 
                {formatPrice(product.price)}
              </span>
            )}
          </div>
          
          {/* Estoque */}
          <div className="flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span className={`text-xs font-medium ${product.stock > 0 ? 'text-green-700' : 'text-red-700'}`}>
              {product.stock > 0 ? `${product.stock} disponíveis` : 'Indisponível'}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
